/**
 * Chess Service
 * 
 * A service for chess game logic and validation using chess.js library.
 * Implements dependency injection pattern for better testability.
 */

import { Chess, Move, Square } from 'chess.js';
import { ChessPiece, PieceType, PieceColor, ChessMove, MoveStatus, FEN, ChessGameState } from '../../types/chess';

/**
 * Service for chess game logic and validation
 */
class ChessService {
  private game: Chess;
  private silent: boolean;

  /**
   * Create a new ChessService instance
   * @param chessInstance Optional Chess instance (for dependency injection)
   * @param silent Whether to suppress error logging (useful for testing)
   */
  constructor(chessInstance?: Chess, silent: boolean = process.env.NODE_ENV === 'test') {
    this.game = chessInstance || new Chess();
    this.silent = silent;
  }

  /**
   * Factory method to create a ChessService with an initial position
   * @param chessModule Optional Chess instance (for dependency injection)
   * @param silent Whether to suppress error logging
   * @returns A new ChessService instance
   */
  static create(chessModule?: Chess, silent: boolean = process.env.NODE_ENV === 'test'): ChessService {
    const chessInstance = chessModule || new Chess();
    return new ChessService(chessInstance, silent);
  }

  /**
   * Get legal moves for a specific square
   * @param square The square to get moves for (e.g., 'e2')
   * @returns Array of legal destination squares
   */
  getLegalMoves(square: string): string[] {
    try {
      const moves = this.game.moves({
        square: square as Square,
        verbose: true
      });
      return moves.map(move => move.to);
    } catch (error) {
      if (!this.silent) {
        console.error(`Error getting legal moves for ${square}:`, error);
      }
      return [];
    }
  }

  /**
   * Make a move on the board
   * @param from Starting square
   * @param to Destination square
   * @param promotion Optional promotion piece ('q', 'r', 'b', 'n')
   * @returns The move object if successful, null if illegal
   */
  makeMove(from: string, to: string, promotion?: PieceType): ChessMove | null {
    try {
      const moveResult = this.game.move({
        from: from as Square,
        to: to as Square,
        promotion
      });

      if (!moveResult) return null;

      return this.convertMoveFormat(moveResult);
    } catch (error) {
      if (!this.silent) {
        console.error('Invalid move:', error);
      }
      return null;
    }
  }

  /**
   * Check if a move is legal
   * @param from Starting square
   * @param to Destination square
   * @returns True if move is legal
   */
  isLegalMove(from: string, to: string, promotion?: PieceType): boolean {
    try {
      const legalMoves = this.getLegalMoves(from);
      if (!legalMoves.includes(to)) return false;

      // Additional check for promotion requirement
      const piece = this.getPiece(from);
      const isPromotion = piece?.type === 'p' &&
        ((piece.color === 'w' && to[1] === '8') ||
          (piece.color === 'b' && to[1] === '1'));

      if (isPromotion && !promotion) return false;

      return true;
    } catch (error) {
      if (!this.silent) {
        console.error('Error checking move legality:', error);
      }
      return false;
    }
  }

  /**
   * Get the piece at a specific square
   * @param square The square to check (e.g., 'e4')
   * @returns The piece or null if square is empty
   */
  getPiece(square: string): ChessPiece | null {
    try {
      const piece = this.game.get(square as Square);
      if (!piece) return null;

      return {
        type: piece.type as PieceType,
        color: piece.color as PieceColor,
        square
      };
    } catch (error) {
      if (!this.silent) {
        console.error(`Error getting piece at ${square}:`, error);
      }
      return null;
    }
  }

  /**
   * Get current board position as array of pieces
   * @returns Array of pieces with their positions
   */
  getPosition(): ChessPiece[] {
    const position: ChessPiece[] = [];
    const squares = [
      'a8', 'b8', 'c8', 'd8', 'e8', 'f8', 'g8', 'h8',
      'a7', 'b7', 'c7', 'd7', 'e7', 'f7', 'g7', 'h7',
      'a6', 'b6', 'c6', 'd6', 'e6', 'f6', 'g6', 'h6',
      'a5', 'b5', 'c5', 'd5', 'e5', 'f5', 'g5', 'h5',
      'a4', 'b4', 'c4', 'd4', 'e4', 'f4', 'g4', 'h4',
      'a3', 'b3', 'c3', 'd3', 'e3', 'f3', 'g3', 'h3',
      'a2', 'b2', 'c2', 'd2', 'e2', 'f2', 'g2', 'h2',
      'a1', 'b1', 'c1', 'd1', 'e1', 'f1', 'g1', 'h1'
    ];

    for (const square of squares) {
      const piece = this.getPiece(square);
      if (piece) {
        position.push(piece);
      }
    }

    return position;
  }

  /**
   * Get current FEN string
   * @returns FEN representation of current position
   */
  getFen(): FEN {
    return this.game.fen() as FEN;
  }

  /**
   * Set the FEN string
   * @param fen FEN string to set
   */
  setFen(fen: string): void {
    this.game = new Chess(fen);
  }

  /**
   * Reset the board to starting position or specified FEN
   * @param fen Optional FEN string to set position
   */
  resetBoard(fen?: string): void {
    // Validate the FEN string if provided
    const validFen = this.validateFEN(fen);
    this.game = new Chess(validFen);
  }

  /**
   * Check if game is in checkmate state
   * @returns True if checkmate
   */
  isCheckmate(): boolean {
    return this.game.isCheckmate();
  }

  /**
   * Check if game is in draw state
   * @returns True if draw
   */
  isDraw(): boolean {
    return this.game.isDraw();
  }

  /**
   * Check if the specified color is in check
   * @returns True if in check
   */
  isInCheck(): boolean {
    return this.game.isCheck();
  }

  /**
   * Get move history in standard algebraic notation
   * @returns Array of moves in SAN
   */
  getMoveHistory(): string[] {
    return this.game.history();
  }

  /**
   * Get detailed move history with additional information
   * @returns Array of detailed move objects
   */
  getDetailedMoveHistory(): ChessMove[] {
    const moves = this.game.history({ verbose: true });
    return moves.map(move => this.convertMoveFormat(move));
  }

  /**
   * Get the current game state
   * @returns Current game state object
   */
  getGameState(): ChessGameState {
    return {
      position: this.getFen(),
      turn: this.game.turn() as PieceColor,
      inCheck: this.game.isCheck(),
      isCheckmate: this.game.isCheckmate(),
      isDraw: this.game.isDraw(),
      isStalemate: this.game.isStalemate(),
      isGameOver: this.game.isGameOver(),
      moveHistory: this.getDetailedMoveHistory()
    };
  }

  /**
   * Attempt to execute a move in standard algebraic notation
   * @param san Move in standard algebraic notation (e.g., "Nf3")
   * @returns The move object if successful, null if illegal
   */
  makeSanMove(san: string): ChessMove | null {
    try {
      // First try the move exactly as provided
      let moveResult;
      try {
        moveResult = this.game.move(san);
        if (moveResult) return this.convertMoveFormat(moveResult);
      } catch (originalError) {
        if (!this.silent) {
          console.warn(`Direct move attempt failed: ${san}`, originalError);
        }
      }
      
      // If move failed and contains check/checkmate symbols, try without them
      if (san.includes('+') || san.includes('#')) {
        const sanWithoutCheck = san.replace(/[+#]/g, '');
        try {
          const altMoveResult = this.game.move(sanWithoutCheck);
          if (altMoveResult) {
            if (!this.silent) {
              console.log(`Successfully executed move without check symbol: ${sanWithoutCheck}`);
            }
            return this.convertMoveFormat(altMoveResult);
          }
        } catch (innerError) {
          if (!this.silent) {
            console.warn(`Failed to execute move without check symbols: ${sanWithoutCheck}`, innerError);
          }
        }
        
        // Additional fallback: try with move coordinates
        try {
          const tempGame = new Chess(this.game.fen());
          // Try the move without check in a temp game to get from/to
          const tempMove = tempGame.move(sanWithoutCheck);
          if (tempMove) {
            // Use the coordinates directly in the real game
            const result = this.makeMove(tempMove.from, tempMove.to, tempMove.promotion as PieceType);
            if (result) {
              if (!this.silent) {
                console.log(`Successfully executed using coordinates from: ${tempMove.from} to: ${tempMove.to}`);
              }
              return result;
            }
          }
        } catch (coordError) {
          if (!this.silent) {
            console.warn(`Failed coordinate-based fallback for: ${san}`, coordError);
          }
        }
      } 
      // If move failed and doesn't contain check symbols, try adding check
      else {
        try {
          // Try with check
          const altMoveResult = this.game.move(san + '+');
          if (altMoveResult) return this.convertMoveFormat(altMoveResult);
        } catch (innerError) {
          if (!this.silent) {
            console.warn(`Failed to execute move with check symbol: ${san}+`, innerError);
          }
        }
      }
      
      // If all else fails, try using our from-to conversion logic
      try {
        const tempGame = new Chess(this.game.fen());
        const moveAttempt = tempGame.move(san);
        
        if (moveAttempt) {
          // If we can parse it in a temporary game, use the from/to coordinates
          const result = this.makeMove(moveAttempt.from, moveAttempt.to, moveAttempt.promotion as PieceType);
          return result;
        }
      } catch (convError) {
        if (!this.silent) {
          console.warn(`Failed to convert SAN to from/to coordinates: ${san}`, convError);
        }
      }
      
      if (!this.silent) {
        console.error(`Invalid SAN move: ${san}`, new Error(`Invalid move: ${san}`));
      }
      return null;
    } catch (error) {
      if (!this.silent) {
        console.error(`Invalid SAN move: ${san}`, error);
      }
      return null;
    }
  }

  /**
   * Undo the last move
   * @returns The move that was undone, or null if no moves to undo
   */
  undoLastMove(): ChessMove | null {
    try {
      const move = this.game.undo();
      if (!move) return null;
      return this.convertMoveFormat(move);
    } catch (error) {
      if (!this.silent) {
        console.error('Error undoing last move:', error);
      }
      return null;
    }
  }

  /**
   * Check if a move would result in check
   * @param from Starting square
   * @param to Destination square
   * @returns True if the move would result in putting the opponent's king in check
   */
  moveResultsInCheck(from: string, to: string, promotion?: PieceType): boolean {
    try {
      // Create a new chess instance to avoid modifying the current game
      const tempGame = new Chess(this.getFen());

      // Get the moving piece's color
      const piece = tempGame.get(from as Square);
      if (!piece) return false;

      // Make the move
      const moveResult = tempGame.move({
        from: from as Square,
        to: to as Square,
        promotion
      });

      if (!moveResult) return false;

      // After making a move, the turn switches to the opponent
      // So isCheck() will tell us if the opponent's king is in check
      return tempGame.isCheck();
    } catch (error) {
      if (!this.silent) {
        console.error('Error checking if move results in check:', error);
      }
      return false; // Invalid move
    }
  }

  /**
   * Convert chess.js move format to our ChessMove format
   * @param move The chess.js move object
   * @returns Our ChessMove format
   */
  private convertMoveFormat(move: Move): ChessMove {
    return {
      from: move.from,
      to: move.to,
      promotion: move.promotion as PieceType | undefined,
      piece: move.piece as PieceType,
      color: move.color as PieceColor,
      flags: move.flags,
      san: move.san
    };
  }

  /**
   * Get the internal Chess instance (for advanced use cases)
   * @returns The underlying Chess instance
   */
  getChessInstance(): Chess {
    return this.game;
  }

  /**
   * Validate a FEN string, particularly checking the en-passant square
   * @param fen FEN string to validate
   * @returns Valid FEN string or undefined if invalid
   */
  validateFEN(fen?: string): string | undefined {
    if (!fen) return undefined;
    
    try {
      const parts = fen.split(' ');
      if (parts.length !== 6) return fen; // Not a standard FEN format
      
      // Check and fix the en-passant square (4th component)
      const enPassantIndex = 3;
      const enPassant = parts[enPassantIndex];
      
      // If it's not "-" and not a valid square (a-h)(1-8), replace with "-"
      if (enPassant !== '-' && !/^[a-h][1-8]$/.test(enPassant)) {
        parts[enPassantIndex] = '-';
        return parts.join(' ');
      }
      
      return fen;
    } catch (error) {
      console.error('Error validating FEN:', error);
      return undefined;
    }
  }

  // ----------------------------------------
  // Sequence-related methods (Week 5)
  // ----------------------------------------

  /**
   * Validates if a move matches the expected move in a sequence
   * 
   * @param position Starting position in FEN notation
   * @param move Move to validate (can be in various formats)
   * @param expectedMove Expected move in algebraic notation
   * @returns True if the move matches the expected move, false otherwise
   */
  validateMoveInSequence(position: string, move: string, expectedMove: string): boolean {
    try {
      // Create a temporary chess instance with the provided position
      const tempChess = new Chess(this.validateFEN(position));
      
      // Special case for exact match (common case)
      if (move === expectedMove) {
        return true;
      }
      
      // Normalize moves by removing check symbols for comparison
      const normalizedMove = move.replace(/[+#]/g, '');
      const normalizedExpected = expectedMove.replace(/[+#]/g, '');
      
      // Compare without check symbols
      if (normalizedMove === normalizedExpected) {
        return true;
      }
      
      // Try to normalize both moves for more flexible matching
      let playerMove: string | null = null;
      let expectedMoveNorm: string | null = null;
      
      // Try to make the moves to get normalized SAN
      try {
        // Make a copy of the chess board
        const playerMoveCopy = new Chess(tempChess.fen());
        // Try with and without check symbols
        let playerMoveResult = playerMoveCopy.move(move);
        if (!playerMoveResult) {
          playerMoveResult = playerMoveCopy.move(normalizedMove);
        }
        if (playerMoveResult) {
          playerMove = playerMoveResult.san.replace(/[+#]/g, '');
        }
      } catch (e) {
        console.warn("Error normalizing player move:", e);
      }
      
      try {
        // Make a copy of the chess board
        const expectedMoveCopy = new Chess(tempChess.fen());
        // Try with and without check symbols
        let expectedMoveResult = expectedMoveCopy.move(expectedMove);
        if (!expectedMoveResult) {
          expectedMoveResult = expectedMoveCopy.move(normalizedExpected);
        }
        if (expectedMoveResult) {
          expectedMoveNorm = expectedMoveResult.san.replace(/[+#]/g, '');
        }
      } catch (e) {
        console.warn("Error normalizing expected move:", e);
      }
      
      // Compare normalized moves if available
      if (playerMove && expectedMoveNorm) {
        return playerMove === expectedMoveNorm;
      }
      
      // If we couldn't normalize, try a more basic comparison by extracting key parts
      const simplifyMove = (m: string): string => {
        // Handle castling
        if (m.toUpperCase().includes('O-O')) {
          return m.toUpperCase();
        }
        
        // Extract piece type (first character if uppercase), destination square (last 2 characters)
        // and capture indicator (x)
        let piece = '';
        let dest = '';
        let isCapture = m.includes('x');
        
        // Get piece type (KQRBNP)
        const pieceMatch = m.match(/^[KQRBNP]/);
        if (pieceMatch) {
          piece = pieceMatch[0];
        }
        
        // Get destination square (e.g., e4, f7)
        const destMatch = m.match(/[a-h][1-8](?!\+|#)/);
        if (destMatch) {
          dest = destMatch[0];
        }
        
        return `${piece}${isCapture ? 'x' : ''}${dest}`;
      };
      
      const simplePlayer = simplifyMove(move);
      const simpleExpected = simplifyMove(expectedMove);
      
      if (simplePlayer && simpleExpected) {
        return simplePlayer === simpleExpected;
      }
      
      // If all else fails, use the first implementation
      // Normalize the expected move
      const normalizedExpectedMove = this.normalizeMove(tempChess, expectedMove);
      if (!normalizedExpectedMove) return false;
      
      // Normalize the actual move
      const normalizedMoveResult = this.normalizeMove(tempChess, move);
      if (!normalizedMoveResult) return false;
      
      // Compare normalized moves - strip check symbols for final comparison
      return normalizedMoveResult.replace(/[+#]/g, '') === normalizedExpectedMove.replace(/[+#]/g, '');
    } catch (error) {
      if (!this.silent) {
        console.error('Error validating move in sequence:', error);
      }
      return false;
    }
  }
  
  /**
   * Gets the resulting position after applying a sequence of moves
   * 
   * @param startingPosition Starting position in FEN notation
   * @param moves Array of moves in algebraic notation
   * @returns Resulting FEN position, or null if any move in the sequence is invalid
   */
  getPositionAfterSequence(startingPosition: string, moves: string[]): string | null {
    try {
      // Create a temporary chess instance with the provided position
      const tempChess = new Chess(this.validateFEN(startingPosition));
      
      // Apply each move in sequence
      for (const move of moves) {
        const result = tempChess.move(move);
        if (!result) {
          if (!this.silent) {
            console.error(`Invalid move in sequence: ${move}`);
          }
          return null;
        }
      }
      
      // Return the resulting position
      return tempChess.fen();
    } catch (error) {
      if (!this.silent) {
        console.error('Error applying move sequence:', error);
      }
      return null;
    }
  }
  
  /**
   * Validates a partially completed sequence against expected moves
   * 
   * @param startingPosition Starting position in FEN notation
   * @param expectedMoves Array of expected moves in algebraic notation
   * @param actualMoves Array of actual moves made
   * @returns Object with validation results
   */
  validatePartialSequence(
    startingPosition: string, 
    expectedMoves: string[], 
    actualMoves: string[]
  ): { 
    isCorrect: boolean; 
    matchedMoves: number;
    score: number; // 0-100 based on completion percentage
  } {
    try {
      // Create a temporary chess instance with the provided position
      const tempChess = new Chess(this.validateFEN(startingPosition));
      
      // Initialize result
      let matchedMoves = 0;
      let isCorrect = true;
      
      // Validate each move in the sequence
      for (let i = 0; i < actualMoves.length; i++) {
        // If we've gone beyond the expected moves, remaining moves are incorrect
        if (i >= expectedMoves.length) {
          isCorrect = false;
          break;
        }
        
        // Get the normalized expected move at this position
        const expectedMove = this.normalizeMove(tempChess, expectedMoves[i]);
        if (!expectedMove) {
          isCorrect = false;
          break;
        }
        
        // Get the normalized actual move
        const actualMove = this.normalizeMove(tempChess, actualMoves[i]);
        if (!actualMove) {
          isCorrect = false;
          break;
        }
        
        // Compare moves
        if (actualMove === expectedMove) {
          matchedMoves++;
          
          // Apply the move to update the position for next comparison
          tempChess.move(actualMove);
        } else {
          isCorrect = false;
          break;
        }
      }
      
      // Calculate score (0-100) based on completion percentage
      const completionPercentage = expectedMoves.length > 0 
        ? (matchedMoves / expectedMoves.length) * 100 
        : 0;
      
      // Round to nearest integer
      const score = Math.round(completionPercentage);
      
      return {
        isCorrect: isCorrect && matchedMoves === expectedMoves.length,
        matchedMoves,
        score
      };
    } catch (error) {
      if (!this.silent) {
        console.error('Error validating partial sequence:', error);
      }
      return {
        isCorrect: false,
        matchedMoves: 0,
        score: 0
      };
    }
  }
  
  /**
   * Helper method to normalize a move into standard algebraic notation
   * Handles different input formats like 'e2e4', 'e2-e4', 'Pe2e4', etc.
   * 
   * @param chess Chess instance with the current position
   * @param moveStr Move string to normalize
   * @returns Normalized move string, or null if invalid
   */
  private normalizeMove(chess: Chess, moveStr: string): string | null {
    try {
      // If it's already in SAN, just return it
      // chess.js doesn't support the sloppy option in TypeScript definition
      // but it does support it in the actual implementation
      const directMove = chess.move(moveStr);
      if (directMove) {
        chess.undo();
        return directMove.san;
      }
      
      // Try to parse as a from-to format (e.g., 'e2e4', 'e2-e4')
      const fromToPattern = /^([a-h][1-8])[- ]?([a-h][1-8])([qrbnQRBN])?$/;
      const fromToMatch = moveStr.match(fromToPattern);
      
      if (fromToMatch) {
        const [_, from, to, promotion] = fromToMatch;
        
        const move = chess.move({
          from: from as Square,
          to: to as Square,
          promotion: promotion as any
        });
        
        if (move) {
          chess.undo();
          return move.san;
        }
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }
}

export default ChessService; 